import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { ChevronLeft, Loader2 } from "lucide-react";

export default function ProjectCreate() {
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", domain: "", pack_slug: "home_services", brand_name: "",
    logo_url: "",
    monetization_type: "hybrid", drip_threads_per_day: 2,
    wp_url: "", wp_username: "", wp_app_password: "",
  });

  useEffect(() => { api.get("/packs").then((r) => setPacks(r.data)).catch(() => {}); }, []);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    const payload = {
      name: form.name, domain: form.domain || null,
      pack_slug: form.pack_slug, brand_name: form.brand_name || null,
      logo_url: form.logo_url || null,
      monetization_type: form.monetization_type,
      drip_threads_per_day: Number(form.drip_threads_per_day) || 2,
    };
    if (form.wp_url && form.wp_username && form.wp_app_password) {
      payload.wp_credentials = {
        url: form.wp_url, username: form.wp_username,
        application_password: form.wp_app_password,
      };
    }
    try {
      const { data } = await api.post("/projects", payload);
      toast.success("Project created.");
      navigate(`/projects/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create project");
    } finally {
      setBusy(false);
    }
  }

  const Input = ({ label, name, type = "text", testid, placeholder, required }) => (
    <label className="block">
      <div className="text-[10px] txt-mono uppercase tracking-widest text-muted-foreground mb-1">{label}{required && " *"}</div>
      <input
        data-testid={testid}
        type={type} required={required}
        value={form[name]} onChange={(e) => set(name, e.target.value)}
        placeholder={placeholder}
        className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary/30 outline-none px-3 py-2 text-sm rounded-sm txt-mono"
      />
    </label>
  );

  return (
    <div className="p-6 lg:p-10 max-w-3xl">
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-6">
        <ChevronLeft className="w-3 h-3" /> Back to projects
      </Link>
      <h1 className="text-3xl font-semibold tracking-tight mb-1">New project</h1>
      <p className="text-sm text-muted-foreground mb-8">One project = one forum site, one WordPress install, one vertical pack.</p>

      <form onSubmit={submit} data-testid={TESTIDS.createProjectForm} className="space-y-8">
        <section className="space-y-4">
          <h2 className="text-xs txt-mono uppercase tracking-widest text-primary">Identity</h2>
          <Input label="Project name" name="name" testid={TESTIDS.cpName} placeholder="HouseTalk US" required />
          <Input label="Domain (public URL)" name="domain" testid={TESTIDS.cpDomain} placeholder="https://housetalk.us.com" />
          <Input label="Brand name (shown in JSON-LD)" name="brand_name" testid={TESTIDS.cpBrandName} placeholder="HouseTalk" />
          <Input label="Logo URL (square, public)" name="logo_url" testid="cp-logo-input" placeholder="https://yoursite.com/wp-content/uploads/logo.png" />
          <label className="block">
            <div className="text-[10px] txt-mono uppercase tracking-widest text-muted-foreground mb-1">Vertical pack *</div>
            <select
              data-testid={TESTIDS.cpPackSelect}
              value={form.pack_slug} onChange={(e) => set("pack_slug", e.target.value)}
              className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm rounded-sm txt-mono"
            >
              {packs.map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.name} — {p.services_count} services / {p.personas_count} personas
                </option>
              ))}
            </select>
          </label>
        </section>

        <section className="space-y-4">
          <h2 className="text-xs txt-mono uppercase tracking-widest text-primary">Monetization & cadence</h2>
          <label className="block">
            <div className="text-[10px] txt-mono uppercase tracking-widest text-muted-foreground mb-1">Monetization type</div>
            <select
              data-testid={TESTIDS.cpMonetization}
              value={form.monetization_type} onChange={(e) => set("monetization_type", e.target.value)}
              className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm rounded-sm txt-mono"
            >
              <option value="hybrid">Hybrid (affiliate + pay-per-call)</option>
              <option value="pay_per_call">Pay-per-call only</option>
              <option value="affiliate">Affiliate only</option>
            </select>
          </label>
          <Input label="Threads published per day (drip)" name="drip_threads_per_day" type="number" testid={TESTIDS.cpDripPerDay} placeholder="2" />
        </section>

        <section className="space-y-4">
          <h2 className="text-xs txt-mono uppercase tracking-widest text-primary">WordPress publication (optional now, required to publish)</h2>
          <p className="text-xs text-muted-foreground">
            Generate an <span className="txt-mono text-foreground">Application Password</span> in WP → Users → Profile → Application Passwords. Stored encrypted in DB.
          </p>
          <Input label="WordPress URL" name="wp_url" testid={TESTIDS.cpWpUrl} placeholder="https://housetalk.us.com" />
          <Input label="WP username (or email)" name="wp_username" testid={TESTIDS.cpWpUser} placeholder="admin" />
          <Input label="Application Password" name="wp_app_password" type="password" testid={TESTIDS.cpWpAppPwd} placeholder="abcd EFGH ijkl MNOP qrst UVWX" />
        </section>

        <div className="flex items-center gap-3 pt-2">
          <button
            data-testid={TESTIDS.cpSubmit}
            type="submit" disabled={busy}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 px-5 py-2 text-sm font-medium rounded-sm transition-colors"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? "Creating…" : "Create project →"}
          </button>
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">Cancel</Link>
        </div>
      </form>
    </div>
  );
}
